import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';

const API_BASE = api.defaults.baseURL.replace(/\/api$/, '');

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

const DT_COLOR = {
  PRESENT:        { bg:'#10b981', text:'#fff' },
  LEAVE:          { bg:'#f59e0b', text:'#fff' },
  CLIENT_HOLIDAY: { bg:'#8b5cf6', text:'#fff' },
  WEEKLY_OFF:     { bg:'#374151', text:'#9ca3af' },
  COMP_OFF:       { bg:'#ec4899', text:'#fff' },
  HALF_DAY:       { bg:'#3b82f6', text:'#fff' },
};

const DT_ABBR = {
  PRESENT:'P', LEAVE:'L', CLIENT_HOLIDAY:'CH',
  WEEKLY_OFF:'WO', COMP_OFF:'CO', HALF_DAY:'HD',
};

// Build all days of a month
const getDaysInMonth = (year, month) => {
  const days = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  return days;
};

// Get day of week for a date (0=Sun)
const getDow = (year, month, day) => new Date(year, month, day).getDay();

// Count working days (Mon-Fri) in month
const countWorkingDays = (year, month) => {
  const days = getDaysInMonth(year, month);
  return days.filter(d => { const dow = getDow(year, month, d); return dow !== 0 && dow !== 6; }).length;
};

export default function Utilization() {
  const now   = new Date();
  const [selYear,  setSelYear]  = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth()); // 0-indexed
  const [employees, setEmployees] = useState([]);
  const [tsData,    setTsData]    = useState([]); // timesheets with entries
  const [loading,   setLoading]   = useState(false);
  const [search,    setSearch]    = useState('');
  const [exporting, setExporting] = useState(false);

  const days         = getDaysInMonth(selYear, selMonth);
  const workingDays  = countWorkingDays(selYear, selMonth);
  const monthLabel   = `${MONTHS[selMonth]} ${selYear}`;

  // Year options: 2024 to current year
  const years = [];
  for (let y = 2024; y <= now.getFullYear(); y++) years.push(y);

  // Month options: only up to current month if same year
  const availableMonths = MONTHS.map((m, i) => ({ label: m, value: i }))
    .filter(m => selYear < now.getFullYear() || m.value <= now.getMonth());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, tsRes] = await Promise.all([
        api.get('/employees'),
        api.get(`/timesheets?month=${selMonth + 1}&year=${selYear}`),
      ]);
      setEmployees(empRes.data.employees || []);
      setTsData(tsRes.data.timesheets || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [selYear, selMonth]);

  useEffect(() => { load(); }, [load]);

  // Adjust month if year changes and month becomes invalid
  useEffect(() => {
    if (selYear === now.getFullYear() && selMonth > now.getMonth()) {
      setSelMonth(now.getMonth());
    }
  }, [selYear]);

  // Build entry map: empId → { day → day_type }
  const entryByEmp = {};
  tsData.forEach(ts => {
    const empId = ts.employee_id;
    if (!entryByEmp[empId]) entryByEmp[empId] = {};
    (ts.entries || []).forEach(e => {
      const d = new Date(e.date);
      if (d.getMonth() === selMonth && d.getFullYear() === selYear) {
        entryByEmp[empId][d.getDate()] = e.day_type;
      }
    });
  });

  // Compute utilization for one employee
  const computeStats = (empId) => {
    const map = entryByEmp[empId] || {};
    let present = 0, leave = 0, holiday = 0, weeklyOff = 0, compOff = 0, halfDay = 0;
    days.forEach(d => {
      const dow = getDow(selYear, selMonth, d);
      const isWknd = dow === 0 || dow === 6;
      const status = map[d] || (isWknd ? 'WEEKLY_OFF' : null);
      if (!status) return;
      if (status === 'PRESENT')        present++;
      else if (status === 'LEAVE')     leave++;
      else if (status === 'CLIENT_HOLIDAY') holiday++;
      else if (status === 'WEEKLY_OFF')weeklyOff++;
      else if (status === 'COMP_OFF')  compOff++;
      else if (status === 'HALF_DAY')  halfDay++;
    });
    const billable  = present + (halfDay * 0.5) + compOff;
    const util      = workingDays > 0 ? ((billable / workingDays) * 100).toFixed(2) : '0.00';
    return { present, leave, holiday, weeklyOff, compOff, halfDay, billable, util };
  };

  // Get effective day status (apply weekend default)
  const getDayStatus = (empId, d) => {
    const map = entryByEmp[empId] || {};
    if (map[d]) return map[d];
    const dow = getDow(selYear, selMonth, d);
    if (dow === 0 || dow === 6) return 'WEEKLY_OFF';
    return null;
  };

  const filtered = employees
    .filter(e => e.is_active)
    .filter(e => {
      // Only show employees with client_approved timesheet this month
      const ts = tsData.find(t => t.employee_id === e.id);
      return ts && ts.status === 'client_approved';
    })
    .filter(e => !search || e.name.toLowerCase().includes(search.toLowerCase()));

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_BASE}/api/reports/utilization-excel?month=${selMonth + 1}&year=${selYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) { alert('Export failed'); return; }
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `Utilization_${MONTHS[selMonth]}_${selYear}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Export error'); }
    finally { setExporting(false); }
  };

  const utilColor = (pct) => {
    const n = parseFloat(pct);
    if (n >= 100) return '#10b981';
    if (n >= 80)  return '#f59e0b';
    if (n >= 50)  return '#ef4444';
    return '#6b7280';
  };

  return (
    <div>
      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Utilization Report</h1>
          <p className="page-sub">Day-wise attendance &amp; utilization — {monthLabel}</p>
        </div>
        <button className="btn btn-primary" disabled={exporting} onClick={handleExport}>
          {exporting ? 'Exporting…' : '📥 Export Excel'}
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="card" style={{ padding:'14px 18px', marginBottom:18, display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
        <div className="field" style={{ flex:'0 0 auto', marginBottom:0 }}>
          <label>Year</label>
          <select value={selYear} onChange={e => setSelYear(Number(e.target.value))} style={{ minWidth:90 }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="field" style={{ flex:'0 0 auto', marginBottom:0 }}>
          <label>Month</label>
          <select value={selMonth} onChange={e => setSelMonth(Number(e.target.value))} style={{ minWidth:130 }}>
            {availableMonths.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div style={{ flex:1, minWidth:200 }}>
          <label style={{ fontSize:12, color:'#9ca3af', display:'block', marginBottom:6 }}>Search Employee</label>
          <input
            className="search-box"
            style={{ width:'100%', maxWidth:'none' }}
            placeholder="Type name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div style={{ marginTop:18, fontSize:13, color:'#6b7280' }}>
          {filtered.length} client approved · {workingDays} working days
        </div>
      </div>

      {/* ── Legend ── */}
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14 }}>
        {Object.entries(DT_ABBR).map(([k, abbr]) => (
          <div key={k} style={{ display:'flex', alignItems:'center', gap:5 }}>
            <div style={{ width:22, height:18, borderRadius:4, background:DT_COLOR[k].bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:700, color:DT_COLOR[k].text }}>{abbr}</div>
            <span style={{ fontSize:11.5, color:'#6b7280' }}>{k.replace(/_/g,' ')}</span>
          </div>
        ))}
        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
          <div style={{ width:22, height:18, borderRadius:4, background:'#1a1d27', border:'1px solid #2a2d3a', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, color:'#6b7280' }}>—</div>
          <span style={{ fontSize:11.5, color:'#6b7280' }}>Not logged</span>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading utilization data…</div>
      ) : filtered.length === 0 ? (
        <div className="card"><div className="empty"><div className="empty-icon">📊</div><p>No client approved timesheets for this month.</p></div></div>
      ) : (
        <div style={{ overflowX:'auto', borderRadius:12, border:'1px solid #2a2d3a' }}>
          <table style={{ minWidth: 200 + days.length * 36, borderCollapse:'collapse', background:'#1a1d27', width:'100%' }}>
            <thead>
              {/* Top header row */}
              <tr style={{ background:'#1F3864' }}>
                <th style={{ ...thStyle, width:36, minWidth:36, position:'sticky', left:0, zIndex:3, background:'#1F3864' }}>#</th>
                <th style={{ ...thStyle, width:180, minWidth:160, position:'sticky', left:36, zIndex:3, background:'#1F3864', textAlign:'left' }}>Employee</th>
                <th style={{ ...thStyle, width:60 }}>Total</th>
                <th style={{ ...thStyle, width:60 }}>W.Days</th>
                <th style={{ ...thStyle, width:72, color:'#10b981' }}>Util%</th>
                <th style={{ ...thStyle, width:38, color:DT_COLOR.PRESENT.bg }}>P</th>
                <th style={{ ...thStyle, width:38, color:DT_COLOR.LEAVE.bg }}>L</th>
                <th style={{ ...thStyle, width:38, color:DT_COLOR.CLIENT_HOLIDAY.bg }}>CH</th>
                <th style={{ ...thStyle, width:38, color:DT_COLOR.WEEKLY_OFF.bg }}>WO</th>
                <th style={{ ...thStyle, width:38, color:DT_COLOR.COMP_OFF.bg }}>CO</th>
                <th style={{ ...thStyle, width:38, color:DT_COLOR.HALF_DAY.bg }}>HD</th>
                {days.map(d => {
                  const dow = getDow(selYear, selMonth, d);
                  const isWknd = dow === 0 || dow === 6;
                  return (
                    <th key={d} style={{ ...thStyle, width:36, minWidth:34, background: isWknd ? '#16213e' : '#1F3864', color: isWknd ? '#6b7280' : '#fff', padding:'4px 2px' }}>
                      <div style={{ fontSize:9, fontWeight:400, color:'#6b7280', lineHeight:1 }}>
                        {['S','M','T','W','T','F','S'][dow]}
                      </div>
                      <div style={{ fontSize:10, fontWeight:700 }}>{d}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filtered.map((emp, idx) => {
                const stats = computeStats(emp.id);
                const tsForEmp = tsData.find(t => t.employee_id === emp.id);
                return (
                  <tr key={emp.id} style={{ borderBottom:'1px solid #22263a' }}
                      onMouseEnter={e => { Array.from(e.currentTarget.cells).forEach(c => c.style.background = 'rgba(255,255,255,0.02)'); }}
                      onMouseLeave={e => { Array.from(e.currentTarget.cells).forEach(c => c.style.background = ''); }}>

                    {/* # */}
                    <td style={{ ...tdStyle, position:'sticky', left:0, zIndex:2, background:'#1a1d27', textAlign:'center', color:'#6b7280', fontSize:11 }}>{idx + 1}</td>

                    {/* Name */}
                    <td style={{ ...tdStyle, position:'sticky', left:36, zIndex:2, background:'#1a1d27', minWidth:160 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                        <div style={{ width:26, height:26, borderRadius:'50%', background:'linear-gradient(135deg,#6c63ff,#4ecdc4)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'#fff', flexShrink:0 }}>
                          {emp.name.charAt(0)}
                        </div>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontSize:12.5, fontWeight:600, color:'#e5e7eb', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:130 }}>{emp.name}</div>
                          {tsForEmp && (
                            <span style={{ fontSize:10, color: tsForEmp.status==='client_approved'?'#10b981': tsForEmp.status==='submitted'||tsForEmp.status==='mgr_approved'||tsForEmp.status==='hr_approved'?'#f59e0b': tsForEmp.status==='rejected'?'#ef4444':'#6b7280' }}>
                              {tsForEmp.status.replace(/_/g,' ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Stats */}
                    <td style={{ ...tdStyle, textAlign:'center', color:'#9ca3af', fontSize:12 }}>{days.length}</td>
                    <td style={{ ...tdStyle, textAlign:'center', color:'#9ca3af', fontSize:12 }}>{workingDays}</td>
                    <td style={{ ...tdStyle, textAlign:'center', fontWeight:700, fontSize:12, color: utilColor(stats.util) }}>
                      {stats.util}%
                    </td>
                    <td style={{ ...tdStyle, textAlign:'center', color:'#10b981', fontWeight:600, fontSize:12 }}>{stats.present}</td>
                    <td style={{ ...tdStyle, textAlign:'center', color:'#f59e0b', fontWeight:600, fontSize:12 }}>{stats.leave}</td>
                    <td style={{ ...tdStyle, textAlign:'center', color:'#8b5cf6', fontWeight:600, fontSize:12 }}>{stats.holiday}</td>
                    <td style={{ ...tdStyle, textAlign:'center', color:'#6b7280', fontWeight:600, fontSize:12 }}>{stats.weeklyOff}</td>
                    <td style={{ ...tdStyle, textAlign:'center', color:'#ec4899', fontWeight:600, fontSize:12 }}>{stats.compOff}</td>
                    <td style={{ ...tdStyle, textAlign:'center', color:'#3b82f6', fontWeight:600, fontSize:12 }}>{stats.halfDay}</td>

                    {/* Day cells */}
                    {days.map(d => {
                      const status = getDayStatus(emp.id, d);
                      const dt     = status ? DT_COLOR[status] : null;
                      const abbr   = status ? DT_ABBR[status] : '—';
                      const dow    = getDow(selYear, selMonth, d);
                      const isWknd = dow === 0 || dow === 6;
                      return (
                        <td key={d} style={{
                          ...tdStyle,
                          textAlign:'center',
                          padding:'3px 1px',
                          background: dt ? `${dt.bg}22` : isWknd ? '#161920' : 'transparent',
                        }}>
                          {status ? (
                            <div style={{
                              display:'inline-flex', alignItems:'center', justifyContent:'center',
                              width:28, height:20, borderRadius:4,
                              background: dt?.bg || 'transparent',
                              color: dt?.text || '#6b7280',
                              fontSize:9, fontWeight:700,
                            }}>{abbr}</div>
                          ) : (
                            <span style={{ fontSize:11, color:'#2a2d3a' }}>—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>

            {/* ── Footer summary row ── */}
            <tfoot>
              <tr style={{ background:'#111827', borderTop:'2px solid #2a2d3a' }}>
                <td colSpan={2} style={{ ...tdStyle, position:'sticky', left:0, zIndex:2, background:'#111827', fontWeight:700, fontSize:12, color:'#fff', paddingLeft:12 }}>
                  TOTAL / DAY COUNT
                </td>
                <td colSpan={9} style={{ ...tdStyle }}></td>
                {days.map(d => {
                  const dow    = getDow(selYear, selMonth, d);
                  const isWknd = dow === 0 || dow === 6;
                  const presentCount = filtered.filter(emp => getDayStatus(emp.id, d) === 'PRESENT').length;
                  const total = filtered.length;
                  const pct   = total > 0 ? Math.round((presentCount / total) * 100) : 0;
                  return (
                    <td key={d} style={{ ...tdStyle, textAlign:'center', padding:'4px 1px', background: isWknd ? '#161920' : '#111827' }}>
                      {!isWknd && (
                        <div>
                          <div style={{ fontSize:10, fontWeight:700, color: pct >= 80 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444' }}>{pct}%</div>
                          <div style={{ fontSize:9, color:'#6b7280' }}>{presentCount}/{total}</div>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: '8px 4px',
  fontSize: 10,
  fontWeight: 700,
  color: '#fff',
  textTransform: 'uppercase',
  letterSpacing: '.3px',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #2a2d3a',
  borderRight: '1px solid rgba(255,255,255,0.05)',
};

const tdStyle = {
  padding: '6px 4px',
  fontSize: 12,
  color: '#d1d5db',
  borderBottom: '1px solid #1f2230',
  borderRight: '1px solid rgba(255,255,255,0.03)',
};